import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { FileText, Clock, Calendar, Loader2, Search, X, Tag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { AiMinutesDoc } from '../../services/FireClientAiMemo';
import { IconPlus } from '../components/FigmaIcons';

interface AiMinutesListProps {
    roomId: string;
    onNewMinutes: () => void;
    onOpenMinutes: (id: string) => void;
}

const AiMinutesList: React.FC<AiMinutesListProps> = ({ roomId, onNewMinutes, onOpenMinutes }) => {
    const [minutesList, setMinutesList] = useState<AiMinutesDoc[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Removed hardcoded roomId

    useEffect(() => {
        if (!roomId) return;
        setIsLoading(true);
        const unsub = Fire.aiMemo.subscribeToMinutes(roomId, (docs) => {
            // 통화 링크 회의록(source: itp-call)은 채널 우측 통화 링크 메뉴에서 표시
            setMinutesList(docs.filter(d => d.source !== 'itp-call'));
            setIsLoading(false);
        });
        return () => unsub();
    }, [roomId]);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowTagDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 전체 태그 수집 (중복 제거, 빈도순)
    const allTags = useMemo(() => {
        const tagCount = new Map<string, number>();
        minutesList.forEach(m => {
            (m.tags || []).forEach(tag => {
                const name = tag.name || '';
                if (name) tagCount.set(name, (tagCount.get(name) || 0) + 1);
            });
        });
        return [...tagCount.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count }));
    }, [minutesList]);

    // 검색어로 태그 필터링 (드롭다운용)
    const filteredTags = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return allTags;
        return allTags.filter(t => t.name.toLowerCase().includes(q));
    }, [allTags, searchQuery]);

    const toggleTag = (tagName: string) => {
        setSelectedTags(prev =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    const removeTag = (tagName: string) => {
        setSelectedTags(prev => prev.filter(t => t !== tagName));
    };

    // 필터링 (태그 AND + 검색어 AND)
    const filteredList = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return minutesList.filter(m => {
            // 태그 AND 필터
            if (selectedTags.length > 0) {
                const mTags = (m.tags || []).map(t => t.name);
                const hasAllTags = selectedTags.every(st => mTags.includes(st));
                if (!hasAllTags) return false;
            }
            // 검색어 필터
            if (q) {
                const title = (m.title || '').toLowerCase();
                const transcript = (m.transcript || '').toLowerCase();
                const tags = (m.tags || []).map(t => (t.name || '').toLowerCase()).join(' ');
                return title.includes(q) || transcript.includes(q) || tags.includes(q);
            }
            return true;
        });
    }, [minutesList, searchQuery, selectedTags]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const mins = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (mins < 1) return t`Just now`;
        if (mins < 60) return t`${mins} mins ago`;
        if (hours < 24) return t`${hours} hours ago`;
        if (days < 7) return t`${days} days ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 w-full space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <Trans>Meeting Minutes</Trans>
                </h2>
                <Button onClick={onNewMinutes}>
                    <IconPlus className="w-4 h-4 mr-2" />
                    <Trans>New Meeting</Trans>
                </Button>
            </div>

            {/* Search + Tag Dropdown */}
            {minutesList.length > 0 && (
                <div ref={searchRef} className="space-y-2">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setShowTagDropdown(true);
                            }}
                            onFocus={() => setShowTagDropdown(true)}
                            placeholder={t`Search or filter by tags...`}
                            className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ai/40 focus:border-ai"
                        />
                        {(searchQuery || selectedTags.length > 0) && (
                            <button
                                onClick={() => { setSearchQuery(''); setSelectedTags([]); }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}

                        {/* Tag Dropdown (inline chips) */}
                        {showTagDropdown && filteredTags.length > 0 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg p-2 max-h-48 overflow-y-auto">
                                <div className="flex flex-wrap gap-1.5">
                                    {filteredTags.map(({ name, count }) => {
                                        const isSelected = selectedTags.includes(name);
                                        return (
                                            <button
                                                key={name}
                                                onClick={() => toggleTag(name)}
                                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${isSelected
                                                        ? 'bg-ai text-on-primary-text'
                                                        : 'bg-muted text-foreground hover:bg-ai/15 hover:text-ai'
                                                    }`}
                                            >
                                                #{name}
                                                <span className={`text-meta-sm ${isSelected ? 'text-on-primary-text' : 'text-muted-foreground'}`}>{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Selected Tags (chips) */}
                    {selectedTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {selectedTags.map(tag => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-meta-sm rounded-full bg-ai text-on-primary-text"
                                >
                                    #{tag}
                                    <button onClick={() => removeTag(tag)} className="hover:bg-ai/80 rounded-full p-0.5">
                                        <X className="w-2.5 h-2.5" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* List */}
            {isLoading ? (
                <div className="text-center py-16">
                    <div className="w-8 h-8 border-2 border-border border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3"><Trans>Loading meeting minutes...</Trans></p>
                </div>
            ) : minutesList.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm"><Trans>No meeting minutes yet. Start a new meeting!</Trans></p>
                    <Button variant="outline" className="mt-4" onClick={onNewMinutes}>
                        <IconPlus className="w-4 h-4 mr-1" />
                        <Trans>Start New Meeting</Trans>
                    </Button>
                </div>
            ) : filteredList.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                    <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm"><Trans>No search results</Trans></p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredList.map(minutes => {
                        const tags = (minutes.tags || []).filter(t => t.name);
                        return (
                            <Card
                                key={minutes.id}
                                className="p-4 cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
                                onClick={() => onOpenMinutes(minutes.id)}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center text-lg shrink-0">
                                        {minutes.icon || '📝'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm truncate">
                                            {minutes.title || t`Meeting Minutes`}
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                            {minutes.transcript?.substring(0, 120) || ''}
                                        </p>

                                        {/* Tags */}
                                        {tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {tags.slice(0, 5).map((tag, i) => (
                                                    <span
                                                        key={i}
                                                        className="px-1.5 py-0 text-meta-sm rounded-full bg-ai-bg text-ai"
                                                    >
                                                        #{tag.name}
                                                    </span>
                                                ))}
                                                {tags.length > 5 && (
                                                    <span className="text-meta-sm text-muted-foreground">+{tags.length - 5}</span>
                                                )}
                                            </div>
                                        )}

                                        <div className="flex items-center gap-3 mt-2 text-meta-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatTime(minutes.duration || 0)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(minutes.created)}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${minutes.status === 'completed'
                                                ? 'bg-badge-success-bg text-badge-success'
                                                : minutes.sttStatus === 'failed'
                                                    ? 'bg-destructive/10 text-destructive'
                                                    : minutes.status === 'processing'
                                                        ? 'bg-badge-info-bg text-badge-info'
                                                        : 'bg-muted text-card-foreground'
                                                }`}>
                                                {minutes.status === 'processing' && minutes.sttStatus !== 'failed' && <Loader2 className="w-3 h-3 animate-spin" />}
                                                {minutes.status === 'completed' ? t`Completed` :
                                                    minutes.sttStatus === 'failed' ? t`Failed` :
                                                        minutes.status === 'processing' ? t`Analyzing` : t`Recording`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AiMinutesList;
